import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cn,
  formatNumber,
  formatPercent,
  timeAgo,
  formatDate,
  formatUptime,
  getStatusColor,
  formatDuration,
  formatBytes,
} from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge, StatusDot } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useAppStore } from '@/store/appStore';
import type {
  Device,
  Alert,
  DeviceFilters,
  PaginatedResponse,
  DeviceStatus,
  AlertSeverity,
  AlertState,
  Client,
  Site,
} from '@/types';
import {
  Search,
  Download,
  RefreshCw,
  SlidersHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Terminal,
  Folder,
  Monitor,
  Power,
  RotateCcw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  CheckSquare,
  Square,
  MinusSquare,
  MoreHorizontal,
  Tag,
  X,
  ExternalLink,
  Cpu,
  HardDrive,
  User,
  Globe,
  Activity,
  Filter,
  Sparkles,
  Play,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Grid,
  List,
  Server,
  Laptop,
  Check,
  AlertOctagon,
  Clock,
  Trash2,
} from 'lucide-react';

type SortField =
  | 'hostname'
  | 'client'
  | 'site'
  | 'loggedInUser'
  | 'os'
  | 'agentVersion'
  | 'lastSeen'
  | 'uptimeSeconds'
  | 'cpu'
  | 'memory'
  | 'disk'
  | 'status'
  | 'patchStatus'
  | 'antivirus';

export function Devices() {
  const navigate = useNavigate();
  const { density, setDensity } = useAppStore();

  // Data states
  const [devicesData, setDevicesData] = useState<PaginatedResponse<Device> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);

  // Filter states
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<DeviceStatus | 'all'>('all');
  const [client, setClient] = useState<string>('all');
  const [site, setSite] = useState<string>('all');
  const [patchStatus, setPatchStatus] = useState<string>('all');
  const [antivirus, setAntivirus] = useState<string>('all');
  const [pendingReboot, setPendingReboot] = useState<'all' | 'yes' | 'no'>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Pagination & Sorting
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortField, setSortField] = useState<SortField>('hostname');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Selection states
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal & Dialog states
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    type: 'restart' | 'shutdown' | 'delete' | 'bulk_restart' | 'bulk_shutdown' | 'bulk_script' | 'bulk_update' | 'bulk_delete' | 'delete_offline';
    device?: Device;
  }>({ open: false, type: 'restart' });

  const [scriptModalOpen, setScriptModalOpen] = useState<boolean>(false);
  const [selectedScript, setSelectedScript] = useState<string>('system_cleanup');
  const [actionProcessing, setActionProcessing] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Show Toast
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Fetch initial lookups
  useEffect(() => {
    async function loadLookups() {
      try {
        const [cls, sts, devResp] = await Promise.all([
          mockApi.getClients(),
          mockApi.getSites(),
          mockApi.getDevices({ pageSize: 500 }),
        ]);
        setClients(cls);
        setSites(sts);

        // Extract unique tags
        const tagsSet = new Set<string>();
        devResp.data.forEach((d: any) => d.tags?.forEach((t: any) => tagsSet.add(t)));
        setAllTags(Array.from(tagsSet));
      } catch (err) {
        console.error('Failed to load initial lookups', err);
      }
    }
    loadLookups();
  }, []);

  // Filtered sites based on selected client
  const availableSites = useMemo(() => {
    if (client === 'all') return sites;
    const foundClient = clients.find((c) => c.name === client || c.id === client);
    if (!foundClient) return sites;
    return sites.filter((s) => s.clientId === foundClient.id);
  }, [client, clients, sites]);

  // Main fetch devices function
  const fetchDevices = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const pendingRebootBool =
        pendingReboot === 'yes' ? true : pendingReboot === 'no' ? false : null;

      const filters: DeviceFilters = {
        search,
        status,
        client,
        site,
        patchStatus,
        antivirus,
        pendingReboot: pendingRebootBool,
        tags: selectedTags,
        sort: sortField,
        order: sortOrder,
        page,
        pageSize,
      };

      const res = await mockApi.getDevices(filters);
      setDevicesData(res);
    } catch (err) {
      console.error('Failed to fetch devices', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [
    search,
    status,
    client,
    site,
    patchStatus,
    antivirus,
    pendingReboot,
    selectedTags,
    sortField,
    sortOrder,
    page,
    pageSize,
  ]);

  useEffect(() => {
    fetchDevices();
    const timer = window.setInterval(() => {
      fetchDevices(true);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [fetchDevices]);

  // Handle column header sort toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // Selection handlers
  const currentDevices = devicesData?.data || [];
  const allCurrentSelected =
    currentDevices.length > 0 && currentDevices.every((d) => selectedIds.has(d.id));
  const isIndeterminate =
    currentDevices.some((d) => selectedIds.has(d.id)) && !allCurrentSelected;

  const handleSelectAll = (checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) {
      currentDevices.forEach((d) => next.add(d.id));
    } else {
      currentDevices.forEach((d) => next.delete(d.id));
    }
    setSelectedIds(next);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    setSelectedIds(next);
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearch('');
    setStatus('all');
    setClient('all');
    setSite('all');
    setPatchStatus('all');
    setAntivirus('all');
    setPendingReboot('all');
    setSelectedTags([]);
    setPage(1);
  };

  const hasActiveFilters =
    search !== '' ||
    status !== 'all' ||
    client !== 'all' ||
    site !== 'all' ||
    patchStatus !== 'all' ||
    antivirus !== 'all' ||
    pendingReboot !== 'all' ||
    selectedTags.length > 0;

  // Toggle tag selection
  const handleToggleTag = (t: string) => {
    setSelectedTags((prev) =>
      prev.includes(t) ? prev.filter((item) => item !== t) : [...prev, t]
    );
    setPage(1);
  };

  // Export CSV
  const handleExportCSV = (devicesToExport?: Device[]) => {
    const list = devicesToExport || currentDevices;
    if (list.length === 0) return;

    const headers = [
      'ID',
      'Hostname',
      'Status',
      'Client',
      'Site',
      'Logged User',
      'OS',
      'IP Address',
      'CPU %',
      'Memory %',
      'Disk %',
      'Patch Status',
      'Antivirus',
      'Pending Reboot',
      'Last Seen',
    ];

    const rows = list.map((d) => [
      d.id,
      d.hostname,
      d.status,
      d.client,
      d.site,
      d.loggedInUser || 'N/A',
      `${d.os} ${d.osVersion}`,
      d.publicIp,
      `${d.cpu}%`,
      `${d.memory}%`,
      `${d.disk}%`,
      d.patchStatus,
      d.antivirus,
      d.pendingReboot ? 'Yes' : 'No',
      d.lastSeen,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `insightnode_devices_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast(`Exported ${list.length} devices to CSV`);
  };

  // Perform confirm modal action
  const handleExecuteModalAction = async () => {
    setActionProcessing(true);
    try {
      if (confirmModal.type === 'delete' && confirmModal.device) {
        const id = confirmModal.device.id;
        await mockApi.deleteDevice(id);
        setDevicesData((prev) =>
          prev
            ? { ...prev, data: prev.data.filter((d) => d.id !== id), total: Math.max(0, prev.total - 1) }
            : prev
        );
        showToast(`Deleted ${confirmModal.device.hostname}. It will reappear if that PC comes back online.`);
        await fetchDevices(true);
      } else if (confirmModal.type === 'delete_offline') {
        const res = await mockApi.deleteOfflineDevices();
        setDevicesData((prev) =>
          prev
            ? { ...prev, data: prev.data.filter((d) => d.status !== 'offline') }
            : prev
        );
        showToast(`Deleted ${res?.deleted ?? 0} offline device(s). They will reappear if those PCs come back online.`);
        await fetchDevices(true);
      } else if (confirmModal.type === 'bulk_delete') {
        const ids = Array.from(selectedIds);
        await Promise.all(ids.map((id) => mockApi.deleteDevice(id)));
        setDevicesData((prev) =>
          prev
            ? { ...prev, data: prev.data.filter((d) => !ids.includes(d.id)), total: Math.max(0, prev.total - ids.length) }
            : prev
        );
        showToast(`Deleted ${ids.length} device(s). They will reappear if those PCs come back online.`);
        setSelectedIds(new Set());
        await fetchDevices(true);
      } else if (confirmModal.type === 'restart') {
        showToast(`Restart command sent to ${confirmModal.device?.hostname}`);
      } else if (confirmModal.type === 'shutdown') {
        showToast(`Shutdown command sent to ${confirmModal.device?.hostname}`);
      } else if (confirmModal.type === 'bulk_restart') {
        showToast(`Restart command sent to ${selectedIds.size} selected devices`);
        setSelectedIds(new Set());
      } else if (confirmModal.type === 'bulk_shutdown') {
        showToast(`Shutdown command sent to ${selectedIds.size} selected devices`);
        setSelectedIds(new Set());
      } else if (confirmModal.type === 'bulk_update') {
        showToast(`Triggered patch updates on ${selectedIds.size} selected devices`);
        setSelectedIds(new Set());
      }
    } catch (err: any) {
      showToast(err?.message || 'Delete failed');
    } finally {
      setActionProcessing(false);
      setConfirmModal({ open: false, type: 'restart' });
    }
  };

  // Execute script bulk action
  const handleExecuteScript = async () => {
    setActionProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setActionProcessing(false);
    setScriptModalOpen(false);
    showToast(`Executing script "${selectedScript}" on ${selectedIds.size} devices`);
    setSelectedIds(new Set());
  };

  // OS Icon helper
  const renderOsIcon = (os: string) => {
    const lower = os.toLowerCase();
    if (lower.includes('win')) {
      return <Server className="h-3.5 w-3.5 text-blue-400 shrink-0" />;
    } else if (lower.includes('mac') || lower.includes('darwin')) {
      return <Laptop className="h-3.5 w-3.5 text-purple-400 shrink-0" />;
    }
    return <Server className="h-3.5 w-3.5 text-emerald-400 shrink-0" />;
  };

  // Mini progress bar helper
  const renderProgressBar = (value: number, label: string) => {
    const pct = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
    let barColor = 'bg-emerald-500';
    let textColor = 'text-[var(--text-secondary)]';

    if (pct >= 90) {
      barColor = 'bg-[var(--status-critical)]';
      textColor = 'text-[var(--status-critical)] font-semibold';
    } else if (pct >= 75) {
      barColor = 'bg-[var(--status-warning)]';
      textColor = 'text-[var(--status-warning)] font-medium';
    }

    return (
      <div className="flex flex-col gap-1 min-w-[70px]">
        <div className="flex items-center justify-between text-2xs">
          <span className="text-[var(--text-muted)]">{label}</span>
          <span className={cn('tabular font-mono', textColor)}>{pct}%</span>
        </div>
        <div className="h-1.5 w-full bg-[var(--hover)] rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', barColor)}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  // Render sort indicator
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-[var(--accent)]" />
    ) : (
      <ArrowDown className="h-3 w-3 text-[var(--accent)]" />
    );
  };

  // Density styles
  const isCompact = density === 'compact';
  const rowPy = isCompact ? 'py-1.5' : 'py-3';
  const tableFontSize = isCompact ? 'text-xs' : 'text-xs md:text-sm';

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6 max-w-[1700px] mx-auto min-h-screen">
      {/* Toast alert */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-[200] bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-primary)] px-4 py-2.5 rounded-lg shadow-xl flex items-center gap-2.5 text-xs animate-in">
          <CheckCircle2 className="h-4 w-4 text-[var(--status-online)]" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[var(--text-primary)]">
              Devices
            </h1>
            <Badge variant="default" className="font-mono text-xs px-2 py-0.5">
              {devicesData ? formatNumber(devicesData.total) : '...'} total
            </Badge>
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Endpoint management, real-time diagnostics, and remote execution
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Density Toggle */}
          <div className="flex items-center bg-[var(--panel)] border border-[var(--border)] rounded-lg p-0.5">
            <button
              onClick={() => setDensity('comfortable')}
              className={cn(
                'px-2.5 py-1 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors',
                density === 'comfortable'
                  ? 'bg-[var(--hover)] text-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              )}
              title="Comfortable row density"
            >
              <List className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Comfortable</span>
            </button>
            <button
              onClick={() => setDensity('compact')}
              className={cn(
                'px-2.5 py-1 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors',
                density === 'compact'
                  ? 'bg-[var(--hover)] text-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              )}
              title="Compact row density"
            >
              <Grid className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Compact</span>
            </button>
          </div>

          <Button
            variant="danger"
            size="sm"
            onClick={() => setConfirmModal({ open: true, type: 'delete_offline' })}
            className="gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span>Delete offline</span>
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchDevices()}
            disabled={loading}
            className="gap-1.5"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={() => handleExportCSV()}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Filter Bar Card */}
      <Card className="p-3.5 md:p-4 bg-[var(--panel)] border-[var(--border)] rounded-lg space-y-3">
        {/* Main Filters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2.5">
          {/* Search */}
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-muted)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search host, user, IP..."
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md pl-8 pr-7 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Status Select */}
          <div>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as DeviceStatus | 'all');
                setPage(1);
              }}
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
            >
              <option value="all">Status: All</option>
              <option value="online">Status: Online</option>
              <option value="warning">Status: Warning</option>
              <option value="critical">Status: Critical</option>
              <option value="offline">Status: Offline</option>
            </select>
          </div>

          {/* Client Select */}
          <div>
            <select
              value={client}
              onChange={(e) => {
                setClient(e.target.value);
                setSite('all');
                setPage(1);
              }}
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
            >
              <option value="all">Client: All</option>
              {clients.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Site Select */}
          <div>
            <select
              value={site}
              onChange={(e) => {
                setSite(e.target.value);
                setPage(1);
              }}
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
            >
              <option value="all">Site: All</option>
              {availableSites.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Patch Status Select */}
          <div>
            <select
              value={patchStatus}
              onChange={(e) => {
                setPatchStatus(e.target.value);
                setPage(1);
              }}
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
            >
              <option value="all">Patch: All</option>
              <option value="compliant">Compliant</option>
              <option value="missing_updates">Missing Updates</option>
              <option value="failed_updates">Failed Updates</option>
            </select>
          </div>

          {/* Antivirus Select */}
          <div>
            <select
              value={antivirus}
              onChange={(e) => {
                setAntivirus(e.target.value);
                setPage(1);
              }}
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
            >
              <option value="all">AV: All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="not_installed">Not Installed</option>
            </select>
          </div>

          {/* Pending Reboot Select */}
          <div>
            <select
              value={pendingReboot}
              onChange={(e) => {
                setPendingReboot(e.target.value as 'all' | 'yes' | 'no');
                setPage(1);
              }}
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] cursor-pointer"
            >
              <option value="all">Reboot: All</option>
              <option value="yes">Reboot Pending</option>
              <option value="no">No Reboot</option>
            </select>
          </div>
        </div>

        {/* Tags Row + Clear Filters */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-[var(--border-subtle)]">
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-[var(--text-muted)]">
            <Tag className="h-3 w-3 text-[var(--text-muted)] shrink-0" />
            <span className="mr-1 text-2xs font-medium uppercase tracking-wider">Tags:</span>
            {allTags.map((tag) => {
              const active = selectedTags.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() => handleToggleTag(tag)}
                  className={cn(
                    'px-2 py-0.5 rounded text-2xs font-medium border transition-colors',
                    active
                      ? 'bg-[var(--accent-muted)] text-[var(--accent)] border-[var(--accent)]'
                      : 'bg-[var(--bg)] text-[var(--text-secondary)] border-[var(--border)] hover:border-[var(--text-muted)]'
                  )}
                >
                  #{tag}
                </button>
              );
            })}
          </div>

          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              className="text-xs text-[var(--accent)] hover:underline flex items-center gap-1 shrink-0 ml-auto"
            >
              <X className="h-3.5 w-3.5" />
              Reset filters
            </button>
          )}
        </div>
      </Card>

      {/* Bulk Action Floating Bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-3 z-[90] bg-[var(--elevated)] border border-[var(--accent)] text-[var(--text-primary)] px-4 py-2.5 rounded-lg shadow-2xl flex flex-wrap items-center justify-between gap-3 animate-in">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--accent-fg)] text-2xs font-bold">
              {selectedIds.size}
            </span>
            <span>devices selected</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setScriptModalOpen(true)}
              className="gap-1.5 text-xs h-7"
            >
              <Play className="h-3 w-3 text-emerald-400" />
              Run Script
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setConfirmModal({ open: true, type: 'bulk_update' })
              }
              className="gap-1.5 text-xs h-7"
            >
              <ShieldCheck className="h-3 w-3 text-blue-400" />
              Install Updates
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setConfirmModal({ open: true, type: 'bulk_restart' })
              }
              className="gap-1.5 text-xs h-7"
            >
              <RotateCcw className="h-3 w-3 text-amber-400" />
              Restart
            </Button>

            <Button
              variant="danger"
              size="sm"
              onClick={() =>
                setConfirmModal({ open: true, type: 'bulk_shutdown' })
              }
              className="gap-1.5 text-xs h-7"
            >
              <Power className="h-3 w-3" />
              Shutdown
            </Button>

            <Button
              variant="danger"
              size="sm"
              onClick={() =>
                setConfirmModal({ open: true, type: 'bulk_delete' })
              }
              className="gap-1.5 text-xs h-7"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const selectedList = currentDevices.filter((d) => selectedIds.has(d.id));
                handleExportCSV(selectedList);
              }}
              className="gap-1.5 text-xs h-7"
            >
              <Download className="h-3 w-3" />
              Export
            </Button>

            <button
              onClick={handleClearSelection}
              className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors ml-2"
              title="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Devices Table Card */}
      <Card className="surface overflow-hidden border-[var(--border)] rounded-lg flex flex-col">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-left border-collapse min-w-[1200px]">
            <thead>
              <tr className="bg-[var(--elevated)] border-b border-[var(--border)] text-2xs uppercase tracking-wider text-[var(--text-muted)] font-medium select-none">
                {/* Select All Checkbox */}
                <th className="py-2.5 px-3 w-10 text-center">
                  <button
                    onClick={() => handleSelectAll(!allCurrentSelected)}
                    className="p-1 hover:text-[var(--text-primary)] text-[var(--text-secondary)]"
                  >
                    {allCurrentSelected ? (
                      <CheckSquare className="h-4 w-4 text-[var(--accent)]" />
                    ) : isIndeterminate ? (
                      <MinusSquare className="h-4 w-4 text-[var(--accent)]" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </th>

                {/* Hostname */}
                <th
                  onClick={() => handleSort('hostname')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Hostname</span>
                    {renderSortIcon('hostname')}
                  </div>
                </th>

                {/* Status */}
                <th
                  onClick={() => handleSort('status')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Status</span>
                    {renderSortIcon('status')}
                  </div>
                </th>

                {/* Client / Site */}
                <th
                  onClick={() => handleSort('client')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Client / Site</span>
                    {renderSortIcon('client')}
                  </div>
                </th>

                {/* User */}
                <th
                  onClick={() => handleSort('loggedInUser')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Logged User</span>
                    {renderSortIcon('loggedInUser')}
                  </div>
                </th>

                {/* OS & Agent */}
                <th
                  onClick={() => handleSort('os')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>OS / Agent</span>
                    {renderSortIcon('os')}
                  </div>
                </th>

                {/* IP Address */}
                <th className="py-2.5 px-3">IP Address</th>

                {/* CPU */}
                <th
                  onClick={() => handleSort('cpu')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>CPU</span>
                    {renderSortIcon('cpu')}
                  </div>
                </th>

                {/* Memory */}
                <th
                  onClick={() => handleSort('memory')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Memory</span>
                    {renderSortIcon('memory')}
                  </div>
                </th>

                {/* Disk */}
                <th
                  onClick={() => handleSort('disk')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Disk</span>
                    {renderSortIcon('disk')}
                  </div>
                </th>

                {/* Patch & AV */}
                <th className="py-2.5 px-3">Security & Patch</th>

                {/* Last Seen / Uptime */}
                <th
                  onClick={() => handleSort('lastSeen')}
                  className="py-2.5 px-3 cursor-pointer hover:text-[var(--text-primary)] group transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Last Seen</span>
                    {renderSortIcon('lastSeen')}
                  </div>
                </th>

                {/* Actions */}
                <th className="py-2.5 px-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className={cn('divide-y divide-[var(--border-subtle)] font-sans', tableFontSize)}>
              {loading ? (
                Array.from({ length: pageSize > 10 ? 10 : pageSize }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="py-3 px-3 text-center">
                      <div className="h-4 w-4 bg-[var(--hover)] rounded mx-auto" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-4 w-28 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-4 w-16 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-4 w-24 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-4 w-20 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-4 w-24 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-4 w-20 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-3 w-16 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-3 w-16 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-3 w-16 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-4 w-24 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3">
                      <div className="h-3 w-16 bg-[var(--hover)] rounded" />
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="h-4 w-12 bg-[var(--hover)] rounded ml-auto" />
                    </td>
                  </tr>
                ))
              ) : currentDevices.length === 0 ? (
                <tr>
                  <td colSpan={13} className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                      <div className="h-10 w-10 rounded-full bg-[var(--hover)] flex items-center justify-center text-[var(--text-muted)]">
                        <Monitor className="h-5 w-5" />
                      </div>
                      <p className="text-sm font-semibold text-[var(--text-secondary)]">
                        No devices found
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        No devices match your selected search or filter criteria. Try resetting your filters.
                      </p>
                      {hasActiveFilters && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleResetFilters}
                          className="mt-2"
                        >
                          Reset Filters
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                currentDevices.map((device) => {
                  const isSelected = selectedIds.has(device.id);

                  return (
                    <tr
                      key={device.id}
                      className={cn(
                        'group transition-colors hover:bg-[var(--hover)]',
                        isSelected && 'bg-[var(--accent-muted)]/30 hover:bg-[var(--accent-muted)]/50',
                        rowPy
                      )}
                    >
                      {/* Checkbox */}
                      <td className="px-3 text-center">
                        <button
                          onClick={() => handleSelectOne(device.id, !isSelected)}
                          className="p-1 hover:text-[var(--text-primary)] text-[var(--text-secondary)]"
                        >
                          {isSelected ? (
                            <CheckSquare className="h-4 w-4 text-[var(--accent)]" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>
                      </td>

                      {/* Hostname */}
                      <td className="px-3 font-medium text-[var(--text-primary)]">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/devices/${device.id}`)}
                            className="hover:text-[var(--accent)] hover:underline text-left font-mono font-semibold"
                          >
                            {device.hostname}
                          </button>
                          {device.tags && device.tags.length > 0 && (
                            <div className="hidden xl:flex items-center gap-1">
                              {device.tags.slice(0, 2).map((t) => (
                                <span
                                  key={t}
                                  className="text-2xs bg-[var(--elevated)] border border-[var(--border)] px-1.5 py-0.2 rounded text-[var(--text-muted)] font-mono"
                                >
                                  {t}
                                </span>
                              ))}
                              {device.tags.length > 2 && (
                                <span className="text-2xs text-[var(--text-muted)]">
                                  +{device.tags.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-3">
                        <div className="flex items-center gap-1.5">
                          <StatusDot status={device.status} />
                          <span
                            className="capitalize font-medium"
                            style={{ color: getStatusColor(device.status) }}
                          >
                            {device.status}
                          </span>
                        </div>
                      </td>

                      {/* Client / Site */}
                      <td className="px-3">
                        <div className="flex flex-col">
                          <span className="text-[var(--text-primary)] truncate max-w-[120px]">
                            {device.client}
                          </span>
                          <span className="text-2xs text-[var(--text-muted)] truncate max-w-[120px]">
                            {device.site}
                          </span>
                        </div>
                      </td>

                      {/* Logged User */}
                      <td className="px-3">
                        <div className="flex items-center gap-1 text-[var(--text-secondary)]">
                          <User className="h-3 w-3 text-[var(--text-muted)] shrink-0" />
                          <span className="truncate max-w-[100px]">
                            {device.loggedInUser || <span className="text-[var(--text-muted)]">-</span>}
                          </span>
                        </div>
                      </td>

                      {/* OS & Agent */}
                      <td className="px-3">
                        <div className="flex items-center gap-1.5">
                          {renderOsIcon(device.os)}
                          <div className="flex flex-col">
                            <span className="text-[var(--text-primary)] text-2xs font-medium truncate max-w-[100px]">
                              {device.os}
                            </span>
                            <span className="text-2xs text-[var(--text-muted)] font-mono">
                              v{device.agentVersion}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* IP Address */}
                      <td className="px-3 font-mono text-2xs">
                        <div className="flex flex-col">
                          <span className="text-[var(--text-primary)]">{device.publicIp}</span>
                          <span className="text-[var(--text-muted)]">{device.localIp}</span>
                        </div>
                      </td>

                      {/* CPU Bar */}
                      <td className="px-3">
                        {renderProgressBar(device.cpu, 'CPU')}
                      </td>

                      {/* Memory Bar */}
                      <td className="px-3">
                        {renderProgressBar(device.memory, 'RAM')}
                      </td>

                      {/* Disk Bar */}
                      <td className="px-3">
                        {renderProgressBar(device.disk, 'Disk')}
                      </td>

                      {/* Patch & Security */}
                      <td className="px-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <Badge variant={device.patchStatus} className="text-2xs py-0">
                              {device.patchStatus === 'compliant'
                                ? 'Compliant'
                                : device.patchStatus === 'missing_updates'
                                ? 'Missing Updates'
                                : 'Patch Failed'}
                            </Badge>

                            {device.pendingReboot && (
                              <span
                                className="inline-flex items-center text-2xs text-[var(--status-warning)]"
                                title="Reboot required"
                              >
                                <RotateCcw className="h-3 w-3 animate-spin-slow" />
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-1 text-2xs text-[var(--text-muted)]">
                            <Shield className="h-3 w-3" />
                            <span
                              style={{ color: getStatusColor(device.antivirus) }}
                              className="capitalize"
                            >
                              AV: {device.antivirus.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Last Seen / Uptime */}
                      <td className="px-3">
                        <div className="flex flex-col text-2xs">
                          <span className="text-[var(--text-primary)] font-medium">
                            {timeAgo(device.lastSeen)}
                          </span>
                          <span className="text-[var(--text-muted)] font-mono">
                            Up {formatUptime(device.uptimeSeconds)}
                          </span>
                        </div>
                      </td>

                      {/* Action Buttons (Visible on hover or quick menu) */}
                      <td className="px-3 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                          {/* Details */}
                          <button
                            onClick={() => navigate(`/devices/${device.id}`)}
                            className="p-1 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] transition-colors"
                            title="Open Device Details"
                          >
                            <Monitor className="h-3.5 w-3.5" />
                          </button>

                          {/* Remote Desktop */}
                          <button
                            onClick={() => navigate(`/devices/${device.id}/remote-desktop`)}
                            className="p-1 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-blue-400 hover:border-blue-500/50 transition-colors"
                            title="Remote Desktop"
                          >
                            <Monitor className="h-3.5 w-3.5 text-blue-400" />
                          </button>

                          {/* File Explorer */}
                          <button
                            onClick={() => navigate(`/devices/${device.id}/files`)}
                            className="p-1 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-amber-400 hover:border-amber-500/50 transition-colors"
                            title="File Explorer"
                          >
                            <Folder className="h-3.5 w-3.5 text-amber-400" />
                          </button>

                          {/* PowerShell */}
                          <button
                            onClick={() => navigate(`/devices/${device.id}/powershell`)}
                            className="p-1 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-emerald-400 hover:border-emerald-500/50 transition-colors"
                            title="PowerShell Console"
                          >
                            <Terminal className="h-3.5 w-3.5 text-emerald-400" />
                          </button>

                          {/* Restart */}
                          <button
                            onClick={() =>
                              setConfirmModal({
                                open: true,
                                type: 'restart',
                                device,
                              })
                            }
                            className="p-1 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-amber-400 hover:border-amber-500/50 transition-colors"
                            title="Restart Device"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>

                          {device.status === 'offline' && (
                            <button
                              onClick={() =>
                                setConfirmModal({
                                  open: true,
                                  type: 'delete',
                                  device,
                                })
                              }
                              className="p-1 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--status-critical)] hover:border-[var(--status-critical)]/50 transition-colors"
                              title="Delete offline device"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}

                          <button
                            onClick={() =>
                              setConfirmModal({
                                open: true,
                                type: 'shutdown',
                                device,
                              })
                            }
                            className="p-1 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--status-critical)] hover:border-[var(--status-critical)]/50 transition-colors"
                            title="Shutdown Device"
                          >
                            <Power className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {devicesData && devicesData.totalPages > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-[var(--panel)] border-t border-[var(--border)] text-xs text-[var(--text-muted)]">
            <div className="flex items-center gap-3">
              <span>
                Showing{' '}
                <strong className="text-[var(--text-primary)]">
                  {formatNumber((page - 1) * pageSize + 1)}
                </strong>{' '}
                to{' '}
                <strong className="text-[var(--text-primary)]">
                  {formatNumber(Math.min(page * pageSize, devicesData.total))}
                </strong>{' '}
                of <strong className="text-[var(--text-primary)]">{formatNumber(devicesData.total)}</strong> devices
              </span>

              {/* Page Size Selector */}
              <div className="flex items-center gap-1 ml-2">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="bg-[var(--bg)] border border-[var(--border)] rounded px-1.5 py-0.5 text-xs text-[var(--text-primary)] focus:outline-none"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Page Buttons */}
            <div className="flex items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-1 font-mono text-xs px-2">
                <span className="text-[var(--text-primary)] font-semibold">{page}</span>
                <span>/</span>
                <span>{devicesData.totalPages}</span>
              </div>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(devicesData.totalPages, p + 1))}
                disabled={page >= devicesData.totalPages}
                className="h-7 w-7 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Confirmation Modal for Destructive & Bulk Actions */}
      <Modal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, type: 'restart' })}
        title={
          confirmModal.type === 'restart'
            ? 'Confirm Device Restart'
            : confirmModal.type === 'shutdown'
            ? 'Confirm Device Shutdown'
            : confirmModal.type === 'bulk_restart'
            ? 'Confirm Bulk Device Restart'
            : confirmModal.type === 'bulk_shutdown'
            ? 'Confirm Bulk Device Shutdown'
            : confirmModal.type === 'delete'
            ? 'Delete Offline Device'
            : confirmModal.type === 'delete_offline'
            ? 'Delete All Offline Devices'
            : confirmModal.type === 'bulk_delete'
            ? 'Delete Selected Devices'
            : 'Confirm Action'
        }
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setConfirmModal({ open: false, type: 'restart' })}
              disabled={actionProcessing}
            >
              Cancel
            </Button>
            <Button
              variant={
                confirmModal.type.includes('shutdown') || confirmModal.type.includes('delete')
                  ? 'danger'
                  : 'primary'
              }
              onClick={handleExecuteModalAction}
              loading={actionProcessing}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-xs">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-200">
                {confirmModal.type.includes('delete')
                  ? 'This only hides the device'
                  : 'Warning: Operating System Action'}
              </p>
              <p className="mt-1 text-amber-300/80">
                {confirmModal.type.includes('delete')
                  ? 'This hides the device from the portal. If that PC is switched on and the agent is still installed, it will come back on its own.'
                  : 'Executing this command will forcefully interrupt any active user session or background processes running on the target endpoint.'}
              </p>
            </div>
          </div>

          {confirmModal.device && (
            <div className="surface p-3 space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Target Hostname:</span>
                <span className="font-mono font-bold text-[var(--text-primary)]">
                  {confirmModal.device.hostname}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Client / Site:</span>
                <span className="text-[var(--text-secondary)]">
                  {confirmModal.device.client} / {confirmModal.device.site}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Active User:</span>
                <span className="text-[var(--text-secondary)]">
                  {confirmModal.device.loggedInUser || 'None'}
                </span>
              </div>
            </div>
          )}

          {confirmModal.type.startsWith('bulk_') && (
            <div className="surface p-3 text-xs space-y-2">
              <p className="font-medium text-[var(--text-primary)]">
                Selected Targets ({selectedIds.size} devices):
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1 font-mono text-2xs text-[var(--text-secondary)]">
                {currentDevices
                  .filter((d) => selectedIds.has(d.id))
                  .map((d) => (
                    <div key={d.id} className="flex items-center justify-between py-0.5 border-b border-[var(--border-subtle)]">
                      <span>{d.hostname}</span>
                      <span className="text-[var(--text-muted)]">{d.client}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Script Selection Modal for Bulk Script Execution */}
      <Modal
        open={scriptModalOpen}
        onClose={() => setScriptModalOpen(false)}
        title={`Execute Script on ${selectedIds.size} Devices`}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setScriptModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExecuteScript}
              loading={actionProcessing}
              className="gap-1.5"
            >
              <Play className="h-3.5 w-3.5" />
              Run Script
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-xs">
          <div>
            <label className="block font-medium text-[var(--text-secondary)] mb-1">
              Select Script Template
            </label>
            <select
              value={selectedScript}
              onChange={(e) => setSelectedScript(e.target.value)}
              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
            >
              <option value="system_cleanup">Windows System Disk Cleanup (PowerShell)</option>
              <option value="flush_dns">Flush DNS & Reset Winsock (CMD)</option>
              <option value="restart_spooler">Restart Print Spooler Service (PowerShell)</option>
              <option value="update_agent">Force InsightNode Agent Self-Update</option>
              <option value="get_installed_kb">Audit Installed KB Patches</option>
            </select>
          </div>

          <div className="surface p-3 space-y-2">
            <p className="font-semibold text-[var(--text-primary)]">Execution Environment</p>
            <div className="flex justify-between text-[var(--text-muted)]">
              <span>Context:</span>
              <span className="font-mono text-[var(--text-secondary)]">NT AUTHORITY\SYSTEM</span>
            </div>
            <div className="flex justify-between text-[var(--text-muted)]">
              <span>Timeout:</span>
              <span className="font-mono text-[var(--text-secondary)]">300 seconds</span>
            </div>
            <div className="flex justify-between text-[var(--text-muted)]">
              <span>Target Devices:</span>
              <span className="font-mono text-[var(--text-primary)] font-bold">{selectedIds.size}</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
